import isEmpty from 'ramda/src/isEmpty';
import isNil from 'ramda/src/isNil';

/**
 * Checks if a value is null, undefined, or empty.
 *
 * Declared as a type guard on the nil case so the `false` branch narrows to
 * `NonNullable<T>`. That is what lets the idiomatic early return
 * (`if (isNilOrEmpty(x)) { return … }` followed by uses of `x`) type-check under
 * `strictNullChecks`.
 *
 * Caveat: the `true` branch narrows to `null | undefined`, which is inexact for values
 * that are non-nil but empty (`''`, `[]`, `{}`). If you need to work with the value inside
 * the positive branch, use {@link isNotNilOrEmpty} and invert the condition.
 * @param value
 */
export const isNilOrEmpty = <T>(value: T | null | undefined): value is null | undefined =>
  isNil(value) || isEmpty(value);

/**
 * Narrowing counterpart of {@link isNilOrEmpty}. Returns `true` only for
 * values that are neither nil nor empty, and narrows the type to
 * `NonNullable<T>` — handy for `array.filter(isNotNilOrEmpty)` to drop
 * `null`/`undefined` while keeping the element type.
 * @param value
 */
export const isNotNilOrEmpty = <T>(value: T | null | undefined): value is NonNullable<T> =>
  !isNilOrEmpty(value);
